import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import {
    DetailEpisode,
    MovieDetails,
    PersonRole,
    ProgramDetails,
    RecordingDetails,
    SeriesDetails,
    TVSeriesDetailEpisode,
} from 'types/Asset';
import { CollectionRoute, CollectionRouteKeyType, DetailRoutes, DetailRoutesKeyType, DetailRoutesType } from 'types/RouteTypes';
import { HelmetData } from 'types/Common';
import { NavigationAllLanguages } from 'types/Navigation';
import seo from 'config/seo.json';
import { encodeId } from './fnUrl';
import { getLocalStorage } from './fnStorage';
import { getMenuItemPath } from './fnRouting';

/**
 * Every page must state its indexability explicitly
 */
export const ROBOTS_INDEX = 'index, follow';
export const ROBOTS_NOINDEX = 'noindex, nofollow';

const getLanguage = () => getLocalStorage('appLanguage') || seo.defaultLanguage;
const getOgLocale = () => seo.ogLocales[getLanguage()] ?? seo.ogLocales[seo.defaultLanguage];
const getSiteName = () => ApplicationConfig.layout_config?.title || seo.siteName;
const getDetailRoutes = (appLanguage: string): Partial<DetailRoutesType> => DetailRoutes[appLanguage] ?? DetailRoutes[seo.defaultLanguage];

/**
 * develop and feature deploys are publicly crawlable under dev.elisaelamus.ee/web-app/<branch>/, so
 * they must never be indexed and must never claim a canonical — otherwise they compete with
 * production for the same content. An empty BASE_NAME alone does not identify production: bug/*
 * branches also build with an empty BASE_NAME (see ci/environment_init.sh), and the release image is
 * deployed to staging as well. So the page must also be served from the canonical origin. NODE_ENV
 * and CONFIG_ENV cannot be used for this: develop builds with NODE_ENV=production, and a feature
 * branch can select CONFIG_ENV=production.
 */
const isProductionDeploy = () => BASENAME === '' && window.location.origin === seo.canonicalOrigin;

const resolveRobots = (robots: string) => (isProductionDeploy() ? robots : ROBOTS_NOINDEX);

/**
 * Canonical URLs are built from the route only — never from `location.href` — so query strings and
 * hashes cannot fork one page, and from the configured production origin rather than the origin the
 * page happens to be served from.
 */
const canonicalFor = (path: string) => (isProductionDeploy() ? `${seo.canonicalOrigin}${path}` : undefined);
const withSiteName = (title: string) => (title && title !== getSiteName() ? `${title} | ${getSiteName()}` : getSiteName());

const PERSONAL_COLLECTION_KEYS: CollectionRouteKeyType[] = [
    'RECORDING',
    'VOD_PURCHASES',
    'CONTINUE_WATCHING',
    'WATCHLIST_MOVIES',
    'WATCHLIST_SERIES',
];

/**
 * Per-user collections (recordings, purchases, continue watching, watchlists) in any language, with
 * or without a /<section> prefix. Every other collection is editorial and public.
 */
export const isPersonalCollectionPath = (pathname: string) => {
    const path = `${pathname.replace(/\/$/, '')}/`;

    return Object.values(CollectionRoute).some((routes) =>
        PERSONAL_COLLECTION_KEYS.some((key) => routes[key] && path.includes(`/${routes[key]}/`))
    );
};

const decodePath = (pathname: string) => {
    try {
        return decodeURI(pathname);
    } catch {
        return pathname;
    }
};

/**
 * Menu pages resolve under every language's slug (/lastele, /kids, /детям) with identical content.
 * Only the Estonian slug is canonical, and the first menu item is the home page, which canonicalises
 * to /. Returns null when the Estonian equivalent is not known, so the caller can fall back to
 * noindex rather than let a translated slug claim itself.
 */
export const getMenuPageCanonicalPath = (pathname: string, navigation: NavigationAllLanguages): string | null => {
    if (pathname === '/') return pathname;

    const path = decodePath(pathname);
    const menuItem = Object.values(navigation ?? {})
        .flatMap((nav) => nav?.menuItems ?? [])
        .find((item) => getMenuItemPath(item) === path);
    if (!menuItem) return pathname;

    const items = navigation?.[seo.defaultLanguage]?.menuItems ?? [];
    const index = items.findIndex((item) =>
        menuItem.pageId ? item.pageId === menuItem.pageId : Boolean(menuItem.internalView) && item.internalView === menuItem.internalView
    );
    if (index === -1) return null;

    return index === 0 ? '/' : getMenuItemPath(items[index]);
};

export const getHelmetData = (
    title: string,
    description = '',
    robots = ROBOTS_NOINDEX,
    canonicalPath = window.location.pathname
): HelmetData => {
    const resolvedRobots = resolveRobots(robots);
    const isIndexable = resolvedRobots === ROBOTS_INDEX;
    const resolvedTitle = title || seo.defaultTitle;
    const resolvedDescription = description || (isIndexable ? seo.defaultDescription : '');
    const canonicalLink = isIndexable ? canonicalFor(canonicalPath) : undefined;

    return {
        title: withSiteName(title),
        canonicalLink,
        description: resolvedDescription,
        robots: resolvedRobots,
        openGraph: {
            'og:title': resolvedTitle,
            'og:type': 'website',
            'og:description': resolvedDescription || undefined,
            'og:locale': getOgLocale(),
            'og:site_name': getSiteName(),
            'og:url': canonicalLink,
        },
    };
};

/**
 * schema.org structured data. Only ever attached to a page that is genuinely indexable — the builders
 * below are called with the canonical URL, which is itself only set for an indexable page on a
 * production build, so structured data cannot leak onto a noindex page.
 */
type JsonLd = Record<string, unknown>;

const withoutEmpty = (node: JsonLd): JsonLd => {
    const result: JsonLd = {};

    Object.keys(node).forEach((key) => {
        const value = node[key];
        if (value !== undefined && value !== null && value !== '') {
            result[key] = value;
        }
    });

    return result;
};

const personNodes = (names: string[]) => names.map((name) => ({ '@type': 'Person', name }));
const countryNodes = (countries: string[]) => countries.map((name) => ({ '@type': 'Country', name }));

/** schema.org wants an ISO-8601 duration; the API reports whole seconds */
const isoDuration = (seconds: number) => `PT${Math.floor(seconds / 60)}M`;

const getMovieJsonLd = (movie: MovieDetails, url: string): JsonLd => {
    const cast = movie.cast ?? [];
    const named = (role: PersonRole) => cast.filter((person) => person.role === role).map((person) => person.title);
    const actors = named(PersonRole.ACTOR);
    const directors = named(PersonRole.DIRECTOR);

    return withoutEmpty({
        '@context': 'https://schema.org',
        '@type': 'Movie',
        name: movie.title,
        url,
        description: movie.shortDescription || movie.description,
        image: movie.image,
        thumbnailUrl: movie.landscapeImage,
        datePublished: movie.release_year ? String(movie.release_year) : undefined,
        duration: movie.duration ? isoDuration(movie.duration) : undefined,
        genre: movie.genres?.length ? movie.genres : undefined,
        countryOfOrigin: movie.production_countries?.length ? countryNodes(movie.production_countries) : undefined,
        actor: actors.length ? personNodes(actors) : undefined,
        director: directors.length ? personNodes(directors) : undefined,
    });
};

const getSeriesJsonLd = (series: SeriesDetails<DetailEpisode | TVSeriesDetailEpisode>, url: string): JsonLd => {
    const credits = series.credits ?? [];
    const named = (role: PersonRole) => credits.filter((credit) => credit.role === role).map((credit) => credit.person?.name);
    const actors = named(PersonRole.ACTOR).filter(Boolean);
    const directors = named(PersonRole.DIRECTOR).filter(Boolean);

    return withoutEmpty({
        '@context': 'https://schema.org',
        '@type': 'TVSeries',
        name: series.title,
        url,
        description: series.description,
        image: series.image_url,
        thumbnailUrl: series.landscapeImage,
        datePublished: series.releaseYear,
        genre: series.genres?.length ? series.genres : undefined,
        countryOfOrigin: series.productionCountries?.length ? countryNodes(series.productionCountries) : undefined,
        actor: actors.length ? personNodes(actors) : undefined,
        director: directors.length ? personNodes(directors) : undefined,
    });
};

/**
 * Series landing pages are the stable, linkable URL for a title, so they canonicalise to the bare
 * series path and are titled with the series name rather than whichever episode happens to be
 * selected via `?episode=`.
 */
export const getSeriesPageHelmetData = (
    series: SeriesDetails<DetailEpisode | TVSeriesDetailEpisode>,
    appLanguage: string,
    type: 'TV' | 'VOD'
): HelmetData => {
    if (!series) {
        return getHelmetData(null, null);
    }

    const routes = getDetailRoutes(appLanguage);
    const path = (type === 'VOD' ? routes.VOD_SERIES : routes.TV_SERIES).replace(':id', encodeId(series.id));
    const canonicalLink = canonicalFor(path);

    return {
        title: withSiteName(series.title),
        description: series.description,
        canonicalLink,
        robots: resolveRobots(ROBOTS_INDEX),
        jsonLd: canonicalLink ? getSeriesJsonLd(series, canonicalLink) : undefined,
        openGraph: {
            'og:title': series.title,
            'og:type': 'video.tv_show',
            'og:description': series.description,
            'og:locale': getOgLocale(),
            'og:site_name': getSiteName(),
            'og:url': canonicalLink,
            'og:image': series.image_url || series.landscapeImage,
        },
    };
};

export type DetailPageKind = 'movie' | 'program' | 'recording';

/**
 * Indexability, canonical route and og:type are decided by the page that renders the tags, never by
 * inspecting `details.type`. Several detail endpoints are consumed unparsed, so that field is absent
 * at runtime and every type guard silently reports false — which previously handed an indexable
 * canonical for an unrelated content type to program and recording pages.
 */
const detailPageConfig: Record<DetailPageKind, { route: DetailRoutesKeyType; ogType: string; indexable: boolean }> = {
    movie: { route: 'MOVIE', ogType: 'video.movie', indexable: true },
    program: { route: 'PROGRAM', ogType: 'video.tv_show', indexable: true },
    // Recordings belong to a single user's library, so they must never be indexed
    recording: { route: 'RECORDING', ogType: 'video.other', indexable: false },
};

export const getDetailPageHelmetData = (
    details: MovieDetails | ProgramDetails | RecordingDetails,
    appLanguage: string,
    kind: DetailPageKind
): HelmetData => {
    if (!details) {
        return getHelmetData(null, null);
    }

    const { route, ogType, indexable } = detailPageConfig[kind];
    const movie = kind === 'movie' ? (details as MovieDetails) : null;
    const program = kind === 'movie' ? null : (details as ProgramDetails);

    const getTitle = () => {
        if (movie) return movie.title;
        if (kind === 'recording') return program.program?.title || program.title;
        return program.title;
    };

    const getDescription = () => (movie ? movie.shortDescription : program.description);
    const getImage = () => (movie ? movie.image : program.image_url);

    const title = getTitle();
    const description = getDescription();
    const routePath = getDetailRoutes(appLanguage)[route];
    const canonicalLink = indexable && routePath ? canonicalFor(routePath.replace(':id', encodeId(details.id))) : undefined;

    return {
        title: withSiteName(title),
        description,
        canonicalLink,
        robots: resolveRobots(indexable ? ROBOTS_INDEX : ROBOTS_NOINDEX),
        // Program and recording payloads are consumed unparsed, so they lack the fields structured
        // data needs; they get a parser of their own before they get JSON-LD.
        jsonLd: movie && canonicalLink ? getMovieJsonLd(movie, canonicalLink) : undefined,
        openGraph: {
            'og:title': title,
            'og:type': ogType,
            'og:description': description,
            'og:site_name': getSiteName(),
            'og:url': canonicalLink,
            'og:locale': getOgLocale(),
            'og:image': getImage(),
        },
    };
};

/**
 * Playback URLs are gated and carry no crawlable content, noindex
 */
export const getPlayerPageHelmetData = (asset: any): HelmetData => {
    const title = asset?.subtitle || asset?.title || asset?.program?.title;

    return {
        title: withSiteName(title),
        description: null,
        robots: ROBOTS_NOINDEX,
        openGraph: {
            'og:title': title,
            'og:type': 'video.other',
            'og:site_name': getSiteName(),
            'og:locale': getOgLocale(),
        },
    };
};

export const loadScriptAsync = (url: string): Promise<any> =>
    new Promise((resolve, reject) => {
        let r = false;
        const t = document.getElementsByTagName('script')[0];
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.src = url;
        s.async = true;

        // @ts-ignore
        // eslint-disable-next-line no-multi-assign,func-names
        s.onload = s.onreadystatechange = function () {
            // @ts-ignore
            if (!r && (!this.readyState || this.readyState === 'complete')) {
                r = true;
                resolve(this);
            }
        };
        // eslint-disable-next-line no-multi-assign
        s.onerror = s.onabort = reject;
        if (t && t.parentNode) {
            t.parentNode.insertBefore(s, t);
        }
    });
